import { useState, useEffect, useCallback } from 'react'
import type { EmailThread } from '../types'

export function useEmailNotifications() {
  const [permission, setPermission] = useState<NotificationPermission>('default')
  const [isSupported, setIsSupported] = useState(false)

  useEffect(() => {
    // Check if notifications are supported
    setIsSupported('Notification' in window)
    
    if ('Notification' in window) {
      setPermission(Notification.permission)
    }
  }, [])

  const requestPermission = useCallback(async () => {
    if (!isSupported) {
      console.log('⚠️ Notifications not supported in this browser')
      return false
    }

    if (permission === 'granted') {
      return true
    }

    try {
      const result = await Notification.requestPermission()
      setPermission(result)
      
      if (result === 'granted') {
        console.log('✅ Notification permission granted')
        return true
      } else {
        console.log('❌ Notification permission denied')
        return false
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error)
      return false
    }
  }, [isSupported, permission])

  const showNotification = useCallback(
    (email: EmailThread) => {
      if (permission !== 'granted' || !isSupported) {
        return
      }

      const title = `📧 New Email from ${email.fromName || email.fromEmail}`
      const options: NotificationOptions = {
        body: email.subject || '(No Subject)',
        icon: '/gmail-icon.png',
        badge: '/gmail-badge.png',
        tag: email.id, // Prevents duplicate notifications for same email
        requireInteraction: false, // Auto-dismiss after a few seconds
        silent: false,
        data: {
          threadId: email.id,
          url: `/thread/${email.id}`,
        },
      }

      try {
        const notification = new Notification(title, options)

        notification.onclick = function (event) {
          event.preventDefault()
          window.focus()
          window.location.hash = options.data.url
          notification.close()
        }

        // Auto-close after 5 seconds
        setTimeout(() => notification.close(), 5000)

        console.log(`🔔 Notification shown for email: ${email.subject}`)
      } catch (error) {
        console.error('Error showing notification:', error)
      }
    },
    [permission, isSupported]
  )

  const showBatchNotification = useCallback(
    (emails: EmailThread[]) => {
      if (permission !== 'granted' || !isSupported || emails.length === 0) {
        return
      }

      const title = `📧 ${emails.length} New Email${emails.length > 1 ? 's' : ''}`
      
      let body = ''
      if (emails.length === 1) {
        body = `From ${emails[0].fromName || emails[0].fromEmail}: ${emails[0].subject}`
      } else if (emails.length === 2) {
        body = `From ${emails[0].fromName || emails[0].fromEmail} and ${emails[1].fromName || emails[1].fromEmail}`
      } else {
        body = `From ${emails[0].fromName || emails[0].fromEmail}, ${emails[1].fromName || emails[1].fromEmail}, and ${emails.length - 2} other${emails.length - 2 > 1 ? 's' : ''}`
      }

      const options: NotificationOptions = {
        body,
        icon: '/gmail-icon.png',
        badge: '/gmail-badge.png',
        tag: 'batch-emails',
        requireInteraction: false,
        silent: false,
        data: {
          url: '/',
        },
      }

      try {
        const notification = new Notification(title, options)

        notification.onclick = function (event) {
          event.preventDefault()
          window.focus()
          window.location.hash = '/'
          notification.close()
        }

        // Auto-close after 7 seconds
        setTimeout(() => notification.close(), 7000)

        console.log(`🔔 Batch notification shown for ${emails.length} emails`)
      } catch (error) {
        console.error('Error showing batch notification:', error)
      }
    },
    [permission, isSupported]
  )

  return {
    permission,
    isSupported,
    requestPermission,
    showNotification,
    showBatchNotification,
  }
}

